/* eslint-disable no-underscore-dangle */
import { isValid } from 'date-fns';

import {
  getBeneficiaryFDPercent,
  getBeneficiaryUnits,
  getSocietyValueAtDate,
  getSoldUnitsAtDate,
  getVestedPercent,
  getVestedUnits,
} from 'utils/filters';
import { getConsolidationPeriods } from 'utils/consolidationPeriods';
import { Society } from 'types';

import {
  BeneficiaryIncentivePoint,
  BeneficiarySocietySummary,
  BeneficiaryVestingPoint,
  BeneficiaryWithPlans,
} from '../types/BeneficiarySocietyTypes';

export const getUniqueVestingDates = (
  beneficiaries: BeneficiaryWithPlans[]
) => {
  const uniqueDates = new Set<Date>();
  beneficiaries.forEach((beneficiary) => {
    const vestingPeriods = getConsolidationPeriods(
      beneficiary,
      beneficiary?.planData
    );

    const vestingDates = vestingPeriods.map((period) => period.date);

    vestingDates?.forEach((vestingDate) => {
      if (!vestingDate || !isValid(vestingDate)) return;
      uniqueDates.add(new Date(vestingDate));
    });
  });

  const sortedDates = [...uniqueDates].sort(
    (a, b) => a.getTime() - b.getTime()
  );

  return sortedDates;
};

export const getVestedUnitsAtDate = (
  beneficiaries: BeneficiaryWithPlans[],
  date: Date
) => {
  let beneficiaryVestedUnits = 0;

  beneficiaries.forEach((beneficiary) => {
    const vestedUnits = getVestedUnits(
      beneficiary,
      beneficiary?.planData,
      Date.now()
    );
    let soldUnits = 0;
    if (beneficiary?.tenderOffers?.length) {
      soldUnits = getSoldUnitsAtDate(
        beneficiary,
        beneficiary.tenderOffers,
        date as unknown as number
      );
    }
    beneficiaryVestedUnits += vestedUnits - soldUnits;
  });
  return beneficiaryVestedUnits;
};

export const getVestingData = (
  beneficiaries: BeneficiaryWithPlans[],
  summaryData: BeneficiarySocietySummary,
  society: Partial<Society>,
  FDpercent: number
): BeneficiaryVestingPoint[] => {
  const vestingDates = getUniqueVestingDates(beneficiaries);
  const lastVestingDate = vestingDates[vestingDates.length - 1];

  const vestingData: BeneficiaryVestingPoint[] = [];
  vestingDates.forEach((date) => {
    const vestedUnits = getVestedUnitsAtDate(beneficiaries, date);
    const societyValueAtDate = getSocietyValueAtDate(society, date)?.value;
    const currentIncentive =
      (getVestedPercent(vestedUnits, society) * societyValueAtDate) / 100;

    vestingData.push({
      date,
      vested: vestedUnits,
      pending:
        date.getTime() === lastVestingDate.getTime()
          ? 0
          : +(
              (+FDpercent * societyValueAtDate) / 100 -
              currentIncentive
            ).toFixed(0),
      value: societyValueAtDate,
      incentive:
        date.getTime() === lastVestingDate.getTime()
          ? Math.max(+currentIncentive.toFixed(2), summaryData.exercisePrice)
          : +currentIncentive.toFixed(2),
    });
  });

  return vestingData;
};

export const getSummaryData = (
  beneficiaries: BeneficiaryWithPlans[],
  society: Partial<Society>
): BeneficiarySocietySummary => {
  const summary = beneficiaries.reduce(
    (acc, beneficiary) => {
      acc.vestedUnits +=
        getVestedUnits(
          beneficiary,
          beneficiary?.planData,
          new Date() as unknown as number
        ) - (beneficiary?.soldUnits as number) || 0;
      acc.units += getBeneficiaryUnits(beneficiary);
      acc.FDpercent += getBeneficiaryFDPercent(beneficiary, society, true);
      acc.exercisePrice +=
        (beneficiary.amount || 0) -
        (beneficiary?.soldUnits || 0) *
          (beneficiary?.planData?.sharePrice || 0);
      acc.invitations?.push({
        status: beneficiary?.invitationStatus || null,
        planName: beneficiary?.planData?.name,
        hasInvitation: !!beneficiary?.planData?.hasInvitation,
      });

      if (beneficiary?.tenderOffers?.length) {
        const matchedParticipants = beneficiary.tenderOffers.flatMap(
          (tenderOffer) =>
            tenderOffer.participants.filter(
              (participant) => participant.id === beneficiary._id
            )
        );
        acc.tenderOffers?.push(...matchedParticipants);
      }

      return acc;
    },
    {
      units: 0,
      vestedUnits: 0,
      FDpercent: 0,
      exercisePrice: 0,
      invitations: [] as BeneficiarySocietySummary['invitations'],
      tenderOffers: [] as BeneficiarySocietySummary['tenderOffers'],
    }
  );

  return summary;
};

export const getSocietyValuesWithFd = (
  beneficiaries: BeneficiaryWithPlans[],
  societyData: Partial<Society>
) => {
  const result: Array<{
    date: Date | string;
    value: number;
    fdPercent: number;
    [key: string]: unknown;
  }> = [];

  societyData?.societyValues?.forEach((societyValue) => {
    let fdPercent = 0;

    beneficiaries.forEach((beneficiary) => {
      let soldUnits = 0;
      if (beneficiary?.tenderOffers?.length) {
        soldUnits = getSoldUnitsAtDate(
          beneficiary,
          beneficiary.tenderOffers,
          societyValue.date
        );
      }

      const fdValue = getBeneficiaryFDPercent(
        beneficiary,
        societyData,
        !!soldUnits,
        soldUnits
      );

      fdPercent += fdValue;
    });

    result.push({
      ...societyValue,
      fdPercent,
    });
  });

  return result;
};

export const getIncentivesData = (
  beneficiaries: BeneficiaryWithPlans[],
  societyData: Partial<Society>,
  vestingValues: BeneficiaryVestingPoint[],
  FDpercent: number,
  summary: BeneficiarySocietySummary
): BeneficiaryIncentivePoint[] => {
  if (!vestingValues?.length) return [];

  const firstVestingDate = vestingValues[0]?.date;
  const lastVestingDate = vestingValues[vestingValues.length - 1]?.date;

  const societyValuesWithFD = getSocietyValuesWithFd(
    beneficiaries,
    societyData
  );

  const filteredValues = societyValuesWithFD?.filter(
    (societyValue) => new Date(societyValue.date) < new Date(lastVestingDate)
  );

  const result = filteredValues.map((societyValue) => {
    const incentive = +(
      (societyValue.fdPercent * societyValue.value) /
      100
    ).toFixed(2);
    return {
      date: new Date(societyValue.date),
      value: societyValue.value,
      incentive:
        new Date(societyValue.date) > new Date(firstVestingDate)
          ? Math.max(incentive, summary.exercisePrice)
          : incentive,
    };
  });

  const lastSocietyValue = getSocietyValueAtDate(societyData, lastVestingDate);

  if (!lastSocietyValue) return result;

  const lastIncentive = +((+FDpercent * lastSocietyValue?.value) / 100).toFixed(
    2
  );

  result.push({
    date: lastVestingDate,
    value: lastSocietyValue?.value,
    incentive:
      new Date(lastVestingDate) > new Date(firstVestingDate)
        ? Math.max(lastIncentive, summary.exercisePrice)
        : lastIncentive,
  });

  return result;
};
